<template>
  <div class="app-container">
    <!-- Header with Search Filter and Project Summary -->
    <div class="navigate-bar">
      <div class="search-filter-section">
        <!-- Search Bar -->
        <el-input
          v-model="searchQuery"
          placeholder="Search by article name, description, cancer type, tissue type, PubMed ID or dataset ID..."
          prefix-icon="el-icon-search"
          size="medium"
          clearable
          class="search-input"
          @input="handleSearch">
        </el-input>
        
        <!-- Filter Options -->
        <el-select
          v-model="selectedCancerType"
          placeholder="Cancer Type"
          clearable
          size="medium"
          class="filter-select"
          @change="applyFilters">
          <el-option
            v-for="type in cancerTypes"
            :key="type"
            :label="type"
            :value="type">
          </el-option>
        </el-select>
        
        <el-select
          v-model="selectedTissueType"
          placeholder="Tissue Type"
          clearable
          size="medium"
          class="filter-select"
          @change="applyFilters">
          <el-option
            v-for="type in tissueTypes"
            :key="type"
            :label="type"
            :value="type">
          </el-option>
        </el-select>
        
        <el-select
          v-model="selectedDatasetType"
          placeholder="Dataset Type"
          clearable
          size="medium"
          class="filter-select"
          @change="applyFilters">
          <el-option label="Public" value="public">
            <span><i class="el-icon-view" style="color: #67C23A;"></i> Public</span>
          </el-option>
          <el-option v-if="isLoggedIn && currentUser.role !== 'community'" label="Private" value="private">
            <span><i class="el-icon-lock" style="color: #E6A23C;"></i> Private</span>
          </el-option>
          <el-option label="Differential" value="differential">
            <span><i class="el-icon-s-data" style="color: #409EFF;"></i> Differential</span>
          </el-option>
        </el-select>
        
        <el-button
          type="primary"
          icon="el-icon-refresh"
          size="medium"
          @click="clearFilters">
          Reset
        </el-button>
      </div>
      
      <div class="project-summary">
        <span class="project-count">{{ filteredProjects.length }} projects</span>
        <el-pagination
          @current-change="changePage"
          :current-page="currentPage"
          :page-size="itemsPerPage"
          layout="prev, pager, next"
          :total="filteredProjects.length">
        </el-pagination>
      </div>
    </div>

    <!-- Content Section with Project List -->
    <div class="content-section">
      <div class="project-list">
        <div
          v-for="project in paginatedProjects"
          :key="project.datasetid"
          class="modern-card project-card">
          <div class="project-card-content">
            <div class="project-header">
              <div class="project-title-section">
                <h3 class="project-title">{{ project.name }}</h3>
                <div class="project-tags">
                  <el-tag v-if="project.datasetType === 'private'" size="small" class="private-tag">
                    <i class="el-icon-lock"></i> Private
                  </el-tag>
                  <el-tag v-else size="small" class="public-tag">
                    <i class="el-icon-view"></i> Public
                  </el-tag>
                  <el-tag 
                    v-if="project.pubmedId && project.datasetType !== 'private'" 
                    size="small" 
                    class="pubmed-tag"
                    @click.stop="openPubMed(project.pubmedId)"
                  >
                    <i class="el-icon-link"></i> PMID: {{ project.pubmedId }}
                  </el-tag>
                  <el-tag v-if="project.datasetType === 'differential'" size="small" class="differential-tag">
                    <i class="el-icon-s-data"></i> Differential
                  </el-tag>
                  <el-tag size="small" class="cancer-type-tag">{{ formatCancerType(project.cancerType) }}</el-tag>
                  <el-tag size="small" class="tissue-type-tag">{{ project.tissueType }}</el-tag>
                  <el-tag size="small" class="dataset-id-tag">
                    <i class="el-icon-document"></i> No. {{ String(project.datasetid).replace(/^No_?/i, '').padStart(6, '0') }}
                  </el-tag>
                </div>
              </div>
            </div>
            
            <div class="project-description">
              <p :class="{ expanded: expandedDescriptions[project.datasetid] }">
                {{ project.description }}
              </p>
              <span 
                v-if="isTextTruncated(project.description) && !expandedDescriptions[project.datasetid]" 
                class="load-more-indicator"
                @click="toggleDescription(project.datasetid)"
              >
                ... <span class="load-more-text">load more</span>
              </span>
              <span 
                v-if="expandedDescriptions[project.datasetid]" 
                class="load-more-indicator"
                @click="toggleDescription(project.datasetid)"
              >
                <span class="load-more-text">show less</span>
              </span>
            </div>
            
            <div class="project-stats">
              <div class="stat-item">
                <i class="el-icon-s-data stat-icon"></i>
                <span class="stat-label">{{ project.samples }} Samples</span>
              </div>

              <div class="stat-item">
                <i class="el-icon-view stat-icon"></i>
                <span class="stat-label">{{ project.views }} views</span>
              </div>
            </div>
          </div>
          
          <div class="project-card-actions">
            <button class="action-btn action-btn--primary" @click.stop="viewDetails(project)">
              <i class="el-icon-view"></i>
              <span>View Details</span>
            </button>
            <button
              v-if="isAdminMode"
              class="action-btn action-btn--edit"
              @click.stop="openEditDialog(project)"
            >
              <i class="el-icon-edit"></i>
              <span>Edit</span>
            </button>
            <button
              v-if="isAdminMode && project.datasetType === 'private'"
              class="action-btn action-btn--secondary"
              @click.stop="openSliceGuide(project)"
            >
              <i class="el-icon-upload"></i>
              <span>Upload Slices</span>
            </button>
            <button 
              v-if="isAdminMode" 
              class="action-btn action-btn--danger" 
              @click.stop="deleteDataset(project)"
            >
              <i class="el-icon-delete"></i>
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredProjects.length === 0" class="modern-card empty-state">
        <div class="empty-content">
          <i class="el-icon-search empty-icon"></i>
          <h3>No projects found</h3>
          <p>Try adjusting your search criteria or filters</p>
          <el-button type="primary" @click="clearFilters">Clear all filters</el-button>
        </div>
      </div>
    </div>

    <!-- Admin Guide Popup: Upload Slice Images -->
    <el-dialog
      :visible.sync="sliceGuideDialogVisible"
      width="760px"
      :close-on-click-modal="false"
      class="slice-guide-dialog"
    >
      <template slot="title">
        <div style="display:flex; align-items:center; gap:10px;">
          <i class="el-icon-picture-outline" style="font-size:18px; color:#3b82f6;"></i>
          <span style="font-weight:600;">Slice Image Upload Guide</span>
          <span v-if="sliceGuideProject" class="dataset-id">
            {{ sliceGuideDatasetId }}
          </span>
        </div>
      </template>

      <SliceUploadGuide
        :loading="sliceGuideLoading"
        :info="sliceGuideInfo"
        :dataset-id="sliceGuideDatasetId"
        @copy="copyToClipboard"
      />
    </el-dialog>

    <!-- Edit Project Metadata Dialog -->
    <el-dialog
      :visible.sync="editDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      class="edit-project-dialog"
    >
      <template slot="title">
        <div style="display:flex; align-items:center; gap:10px;">
          <i class="el-icon-edit" style="font-size:18px; color:#409EFF;"></i>
          <span style="font-weight:600;">Edit Project Metadata</span>
          <el-tag v-if="editProject" size="small" type="info">{{ editProject.datasetid }}</el-tag>
        </div>
      </template>

      <el-form 
        v-if="editProject"
        :model="editForm" 
        label-width="120px"
        :rules="editFormRules"
        ref="editFormRef"
      >
        <el-form-item label="Name" prop="name">
          <el-input v-model="editForm.name" placeholder="Project name"></el-input>
        </el-form-item>

        <el-form-item label="Description" prop="description">
          <el-input 
            type="textarea" 
            v-model="editForm.description" 
            :rows="4"
            placeholder="Project description"
          ></el-input>
        </el-form-item>

        <el-form-item label="Cancer Type" prop="cancerType">
          <el-input v-model="editForm.cancerType" placeholder="e.g., Lung Cancer"></el-input>
        </el-form-item>

        <el-form-item label="Tissue Type" prop="tissueType">
          <el-input v-model="editForm.tissueType" placeholder="e.g., Lung"></el-input>
        </el-form-item>

        <el-form-item label="PubMed ID" prop="pubmedId">
          <el-input v-model="editForm.pubmedId" placeholder="e.g., 12345678"></el-input>
        </el-form-item>

        <el-form-item label="Dataset Type" prop="datasetType">
          <el-select v-model="editForm.datasetType" placeholder="Select type" style="width: 100%;">
            <el-option label="Public" value="public">
              <span><i class="el-icon-view" style="color: #67C23A;"></i> Public</span>
            </el-option>
            <el-option label="Private" value="private">
              <span><i class="el-icon-lock" style="color: #E6A23C;"></i> Private</span>
            </el-option>
            <el-option label="Differential" value="differential">
              <span><i class="el-icon-s-data" style="color: #409EFF;"></i> Differential</span>
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="saveProjectMetadata" :loading="editSaving">
          Save Changes
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import SliceUploadGuide from '@/components/Widgets/SliceUploadGuide.vue'

export default {
  name: 'ProjectBrowser',
  components: {
    SliceUploadGuide
  },
  props: {
    isLoggedIn: {
      type: Boolean,
      default: false
    },
    currentUser: {
      type: Object,
      default: () => ({ username: '', email: '', role: 'community' })
    }
  },
  data() {
    return {
      searchQuery: '',
      selectedCancerType: '',
      selectedTissueType: '',
      selectedDatasetType: '',
      showSearchFilter: true,
      projects: [],
      filteredProjects: [],
      currentPage: 1,
      itemsPerPage: 5,
      expandedDescriptions: {},
      sliceGuideDialogVisible: false,
      sliceGuideLoading: false,
      sliceGuideProject: null,
      sliceGuideInfo: {
        uuid: '',
        uuid_folder_abs: '',
        slice_root_abs: ''
      },
      // Edit project metadata
      editDialogVisible: false,
      editProject: null,
      editSaving: false,
      editForm: {
        name: '',
        description: '',
        cancerType: '',
        tissueType: '',
        pubmedId: '',
        datasetType: 'public'
      },
      editFormRules: {
        name: [
          { required: true, message: 'Please enter a project name', trigger: 'blur' }
        ]
      }
    }
  },
  mounted() {
    this.fetchProjects()
  },
  computed: {
    isAdminMode() {
      return this.isLoggedIn && this.currentUser.role === 'admin'
    },
    sliceGuideDatasetId() {
      return (this.sliceGuideProject && this.sliceGuideProject.datasetid) ? this.sliceGuideProject.datasetid : 'No_X'
    },
    cancerTypes() {
      return [...new Set(this.projects.map(p => p.cancerType))].sort()
    },
    tissueTypes() {
      return [...new Set(this.projects.map(p => p.tissueType))].sort()
    },
    paginatedProjects() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredProjects.slice(start, end);
    }
  },
  methods: {
    // Extract numeric part from dataset ID (e.g., "No_4" -> 4)
    extractDatasetNumber(datasetid) {
      const match = datasetid.match(/^No_(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    },
    
    async fetchProjects() {
      try {
        const resp = await axios.post('/api/projectSearch/list_projects')
        if (resp && resp.data && resp.data.code === 200) {
          const rows = Array.isArray(resp.data.data) ? resp.data.data : []
          const normalized = rows.map(r => ({
            datasetid: r.datasetid || r.datasetId || '',
            name: r.name || '',
            description: r.description || '',
            pubmedId: r.pubmedId ?? null,
            samples: r.samples ?? 0,
            cancerType: r.cancerType || '',
            tissueType: r.tissueType || '',
            datasetType: r.datasetType || 'public', // Default to public if not specified
            views: r.views ?? 0,
          }))
          
          // Filter out private datasets for users who don't have access
          let filtered = normalized;
          if (!this.isLoggedIn || this.currentUser.role === 'community') {
            filtered = normalized.filter(project => project.datasetType !== 'private');
          }

          // Sort projects numerically by dataset ID
          filtered.sort((a, b) => {
            const numA = this.extractDatasetNumber(a.datasetid);
            const numB = this.extractDatasetNumber(b.datasetid);
            return numA - numB;
          });

          this.projects = filtered
          this.filteredProjects = [...this.projects]
          this.currentPage = 1
        } else {
          this.$notify && this.$notify({
            title: 'Error',
            message: 'Failed to load projects',
            type: 'error',
            position: 'top-right'
          })
        }
      } catch (e) {
        this.$notify && this.$notify({
          title: 'Error',
          message: 'Error loading projects',
          type: 'error',
          position: 'top-right'
        })
      }
    },
    handleSearch() {
      this.applyFilters()
    },
    applyFilters() {
      let filtered = [...this.projects]
      
      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(project =>
          project.name.toLowerCase().includes(query) ||
          project.description.toLowerCase().includes(query) ||
          project.cancerType.toLowerCase().includes(query) ||
          project.tissueType.toLowerCase().includes(query) ||
          project.datasetid.toLowerCase().includes(query) ||
          (project.pubmedId && project.pubmedId.toString().toLowerCase().includes(query))
        )
      }
      
      // Apply cancer type filter
      if (this.selectedCancerType) {
        filtered = filtered.filter(project => project.cancerType === this.selectedCancerType)
      }
      
      // Apply tissue type filter
      if (this.selectedTissueType) {
        filtered = filtered.filter(project => project.tissueType === this.selectedTissueType)
      }
      
      // Apply dataset type filter
      if (this.selectedDatasetType) {
        if (this.selectedDatasetType === 'differential') {
          filtered = filtered.filter(project => project.datasetType === 'differential')
        } else if (this.selectedDatasetType === 'private') {
          // Only apply private filter if user has access
          if (this.isLoggedIn && this.currentUser.role !== 'community') {
            filtered = filtered.filter(project => project.datasetType === 'private')
          } else {
            // Reset filter for community users
            this.selectedDatasetType = ''
          }
        } else if (this.selectedDatasetType === 'public') {
          filtered = filtered.filter(project => project.datasetType === 'public')
        }
      }
      
      this.filteredProjects = filtered
    },
    clearFilters() {
      this.searchQuery = ''
      this.selectedCancerType = ''
      this.selectedTissueType = ''
      this.selectedDatasetType = ''
      this.filteredProjects = [...this.projects]
    },
    toggleSearchFilter() {
      this.showSearchFilter = !this.showSearchFilter
    },
    openProject(project) {
      this.$notify({
        title: 'Opening Project',
        message: `Loading ${project.name}...`,
        type: 'info'
      })
    },
    analyzeProject(project) {
      this.$notify({
        title: 'Starting Analysis',
        message: `Initializing analysis for ${project.name}`,
        type: 'success'
      })
    },
    viewDetails(project) {
      const routeData = this.$router.resolve(`/project/${project.datasetid}`);
      window.open(routeData.href, '_blank');
    },
    openPubMed(pubmedId) {
      window.open(`https://pubmed.ncbi.nlm.nih.gov/${pubmedId}/`, '_blank')
    },
    changePage(page) {
      this.currentPage = page;
      // Scroll to top of content section
      this.$nextTick(() => {
        const contentSection = document.querySelector('.content-section');
        if (contentSection) {
          contentSection.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    },
    isTextTruncated(text) {
      // Rough estimate: check if text is long enough to be truncated
      // Assuming average character width and 3 lines with about 100 chars per line
      return text && text.length > 300;
    },
    toggleDescription(datasetid) {
      this.$set(this.expandedDescriptions, datasetid, !this.expandedDescriptions[datasetid]);
    },
    formatCancerType(cancerType) {
      // Remove abbreviation prefix like "CESC_" from "CESC_Cervical squamous cell carcinoma..."
      if (!cancerType) return ''
      return cancerType.replace(/^[A-Z]{2,}_/, '')
    },
    async deleteDataset(project) {
      this.$confirm(
        `Are you sure you want to delete "${project.name}" (ID: ${project.datasetid})? This will permanently delete the dataset and renumber all subsequent datasets.`,
        'Confirm Delete',
        {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning',
          confirmButtonClass: 'el-button--danger'
        }
      ).then(async () => {
        try {
          const resp = await axios.post('/api/projectSearch/delete_dataset', {
            datasetid: project.datasetid
          })
          
          if (resp && resp.data && resp.data.code === 200) {
            this.$notify({
              title: 'Success',
              message: `Dataset "${project.name}" has been deleted successfully`,
              type: 'success',
              position: 'top-right'
            })
            // Refresh the project list
            await this.fetchProjects()
          } else {
            this.$notify({
              title: 'Error',
              message: resp.data?.error || 'Failed to delete dataset',
              type: 'error',
              position: 'top-right'
            })
          }
        } catch (e) {
          this.$notify({
            title: 'Error',
            message: e.response?.data?.error || 'Error deleting dataset',
            type: 'error',
            position: 'top-right'
          })
        }
      }).catch(() => {
        // User cancelled
      })
    },
    async openSliceGuide(project) {
      this.sliceGuideProject = project
      this.sliceGuideInfo = { uuid: '', uuid_folder_abs: '', slice_root_abs: '' }
      this.sliceGuideDialogVisible = true
      this.sliceGuideLoading = true

      try {
        const resp = await axios.post('/api/projectSearch/get_dataset_storage_info', {
          datasetid: project.datasetid
        })
        if (resp && resp.data && resp.data.code === 200) {
          const d = resp.data.data || {}
          this.sliceGuideInfo = {
            uuid: d.uuid || '',
            uuid_folder_abs: d.uuid_folder_abs || '',
            slice_root_abs: d.slice_root_abs || (d.uuid_folder_abs ? `${d.uuid_folder_abs}/slice` : '')
          }
        } else {
          this.$notify && this.$notify({
            title: 'Error',
            message: resp.data?.error || 'Failed to load dataset storage info',
            type: 'error',
            position: 'top-right'
          })
        }
      } catch (e) {
        this.$notify && this.$notify({
          title: 'Error',
          message: e.response?.data?.error || 'Error loading dataset storage info',
          type: 'error',
          position: 'top-right'
        })
      } finally {
        this.sliceGuideLoading = false
      }
    },
    async copyToClipboard(text) {
      if (!text) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const el = document.createElement('textarea')
          el.value = text
          el.setAttribute('readonly', '')
          el.style.position = 'absolute'
          el.style.left = '-9999px'
          document.body.appendChild(el)
          el.select()
          document.execCommand('copy')
          document.body.removeChild(el)
        }
        this.$message && this.$message.success('Copied')
      } catch (err) {
        this.$message && this.$message.error('Copy failed')
      }
    },
    // Edit project metadata methods
    openEditDialog(project) {
      this.editProject = project
      this.editForm = {
        name: project.name || '',
        description: project.description || '',
        cancerType: project.cancerType || '',
        tissueType: project.tissueType || '',
        pubmedId: project.pubmedId || '',
        datasetType: project.datasetType || 'public'
      }
      this.editDialogVisible = true
    },
    async saveProjectMetadata() {
      // Validate form
      if (this.$refs.editFormRef) {
        try {
          await this.$refs.editFormRef.validate()
        } catch (e) {
          return // Validation failed
        }
      }

      if (!this.editProject) return

      this.editSaving = true
      try {
        // Prepare metadata object (only include fields that have changed)
        const metadata = {}
        if (this.editForm.name !== this.editProject.name) {
          metadata.name = this.editForm.name
        }
        if (this.editForm.description !== this.editProject.description) {
          metadata.description = this.editForm.description
        }
        if (this.editForm.cancerType !== this.editProject.cancerType) {
          metadata.cancerType = this.editForm.cancerType
        }
        if (this.editForm.tissueType !== this.editProject.tissueType) {
          metadata.tissueType = this.editForm.tissueType
        }
        if (String(this.editForm.pubmedId || '') !== String(this.editProject.pubmedId || '')) {
          metadata.pubmedId = this.editForm.pubmedId || null
        }
        if (this.editForm.datasetType !== this.editProject.datasetType) {
          metadata.datasetType = this.editForm.datasetType
        }

        // Check if there are any changes
        if (Object.keys(metadata).length === 0) {
          this.$notify({
            title: 'Info',
            message: 'No changes to save',
            type: 'info',
            position: 'top-right'
          })
          this.editDialogVisible = false
          return
        }

        const resp = await axios.post('/api/projectSearch/update_project_metadata', {
          datasetid: this.editProject.datasetid,
          metadata: metadata
        })

        if (resp && resp.data && resp.data.code === 200) {
          this.$notify({
            title: 'Success',
            message: resp.data.data?.message || 'Project metadata updated successfully',
            type: 'success',
            position: 'top-right'
          })
          this.editDialogVisible = false
          // Refresh the project list
          await this.fetchProjects()
        } else {
          this.$notify({
            title: 'Error',
            message: resp.data?.error || 'Failed to update project metadata',
            type: 'error',
            position: 'top-right'
          })
        }
      } catch (e) {
        this.$notify({
          title: 'Error',
          message: e.response?.data?.error || 'Error updating project metadata',
          type: 'error',
          position: 'top-right'
        })
      } finally {
        this.editSaving = false
      }
    }
  }
}
</script>

<style scoped>
/* App Container - Full Height Layout */
.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

/* Navigate Bar - Search Filter and Project Summary Header */
.navigate-bar {
  display: flex;
  flex-direction: column;
  padding: 20px 3vw 10px 3vw;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.95);
  transition: all 0.3s ease;
  gap: 12px;
}

/* Search Filter Section */
.search-filter-section {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
}

.search-input {
  flex: 1;
  min-width: 200px;
}

.filter-select {
  min-width: 140px;
}

/* Project Summary */
.project-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-top: 4px;
}

.project-count {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}

/* Content Section - Scrollable with Responsive Padding */
.content-section {
  flex: 1;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
  padding-top: clamp(15px, 3vw, 21px);
  padding-bottom: clamp(15px, 3vw, 30px);
  padding-left: max(clamp(15px, 3vw, 30px), calc((100vw - 1600px) / 2 + clamp(15px, 3vw, 30px)));
  padding-right: max(clamp(15px, 3vw, 30px), calc((100vw - 1600px) / 2 + clamp(15px, 3vw, 30px)));
}

/* Project List */
.project-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}


.project-card {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  padding: 20px 24px;
  min-height: 180px;
}

.project-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

/* Project Tags - Cohesive Natural Color Palette */
.project-tags .el-tag {
  font-weight: 500;
  border-radius: 4px;
  font-size: 12px;
}

/* Dataset ID Tag - Neutral slate */
.dataset-id-tag {
  background-color: #f8fafc !important;
  border-color: #e2e8f0 !important;
  color: #64748b !important;
}

/* Private Tag - Soft amber */
.private-tag {
  background-color: #fffbeb !important;
  border-color: #fde68a !important;
  color: #b45309 !important;
}

/* Public Tag - Soft teal */
.public-tag {
  background-color: #f0fdfa !important;
  border-color: #99f6e4 !important;
  color: #0f766e !important;
}

/* Differential Data Tag - Soft indigo */
.differential-tag {
  background-color: #eef2ff !important;
  border-color: #c7d2fe !important;
  color: #4338ca !important;
}

/* Cancer Type Tag - Soft violet */
.cancer-type-tag {
  background-color: #faf5ff !important;
  border-color: #e9d5ff !important;
  color: #7c3aed !important;
}

/* Tissue Type Tag - Soft sky */
.tissue-type-tag {
  background-color: #f0f9ff !important;
  border-color: #bae6fd !important;
  color: #0369a1 !important;
}

/* PubMed Tag - Soft orange, clickable */
.pubmed-tag {
  background-color: #fff7ed !important;
  border-color: #fed7aa !important;
  color: #c2410c !important;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pubmed-tag:hover {
  background-color: #ffedd5 !important;
  border-color: #fdba74 !important;
  color: #9a3412 !important;
}

.project-card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0; /* Allow flex children to shrink */
}

.project-card-content > * {
  flex-shrink: 0; /* Prevent header and stats from shrinking */
}

.project-card-content .project-description {
  flex-shrink: 1; /* Allow description to shrink if needed */
}

.project-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}

.project-title-section {
  flex: 1;
}

.project-title {
  font-size: 19px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 10px 0;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.project-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.project-description {
  color: #6b7280;
  line-height: 1.5;
  font-size: 15px;
  flex: 1;
  overflow: visible;
  position: relative;
}

.project-description p {
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 4.5em; /* 3 lines * 1.5 line-height */
  transition: max-height 0.3s ease;
}

.project-description p.expanded {
  display: block;
  -webkit-line-clamp: unset;
  line-clamp: unset;
  max-height: none;
  overflow: visible;
}

.load-more-indicator {
  display: inline-block;
  margin-top: 4px;
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
  cursor: pointer;
  user-select: none;
}

.load-more-indicator:hover {
  color: #6b7280;
}

.load-more-text {
  color: #3b82f6;
  font-weight: 500;
  font-style: normal;
}

.load-more-text:hover {
  color: #2563eb;
  text-decoration: underline;
}

.project-stats {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6b7280;
  font-size: 13px;
}

.stat-icon {
  font-size: 14px;
  color: #9ca3af;
}

.project-card-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: 24px;
  justify-content: center;
  align-self: center;
}

/* Action Buttons */
.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 140px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.action-btn i {
  font-size: 14px;
}

.action-btn--primary {
  background: #f8fafc;
  color: #3b82f6;
  border: 1px solid #e2e8f0;
}

.action-btn--primary:hover {
  background: #eff6ff;
  color: #2563eb;
  border-color: #bfdbfe;
  transform: translateY(-1px);
}

.action-btn--secondary {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.action-btn--secondary:hover {
  background: #dcfce7;
  color: #15803d;
  border-color: #86efac;
  transform: translateY(-1px);
}

.action-btn--edit {
  background: #fefce8;
  color: #ca8a04;
  border: 1px solid #fef08a;
}

.action-btn--edit:hover {
  background: #fef9c3;
  color: #a16207;
  border-color: #facc15;
  transform: translateY(-1px);
}

.action-btn--danger {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  color: #dc2626;
  border: 1px solid #fecaca;
}

.action-btn--danger:hover {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #b91c1c;
  border-color: #fca5a5;
  transform: translateY(-1px);
}

/* Empty State */
.empty-state {
  padding: 48px 20px;
}

.empty-content {
  text-align: center;
  color: #6b7280;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 12px;
  opacity: 0.4;
}

.empty-content h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 6px 0;
  color: #374151;
}

.empty-content p {
  margin: 0 0 16px 0;
  font-size: 14px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .search-filter-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input {
    width: 100%;
  }
  
  .filter-select {
    width: 100%;
  }
  
  .project-summary {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .project-header {
    flex-direction: column;
    gap: 12px;
  }
  
  .project-card {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
    min-height: auto;
  }
  
  .project-card-actions {
    flex-direction: row;
    flex-wrap: wrap;
    margin-left: 0;
    justify-content: flex-end;
    align-self: stretch;
  }
  
  .action-btn {
    min-width: 120px;
    padding: 8px 12px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .navigate-bar {
    padding: 12px 15px;
  }
  
  .project-stats {
    flex-direction: column;
    gap: 8px;
  }
  
  .project-card-actions {
    flex-direction: column;
  }
  
  .action-btn {
    width: 100%;
    min-width: unset;
  }
}
</style>

<style>
/* Dialog styles - non-scoped to target el-dialog internals */
.slice-guide-dialog .el-dialog__body {
  padding: 20px 24px;
  overflow: hidden;
}

.edit-project-dialog .el-dialog__body {
  padding: 20px 24px;
}

.edit-project-dialog .el-form-item {
  margin-bottom: 18px;
}

.edit-project-dialog .el-form-item__label {
  font-weight: 500;
  color: #374151;
}

.edit-project-dialog .el-input__inner,
.edit-project-dialog .el-textarea__inner {
  border-radius: 6px;
}

.edit-project-dialog .el-textarea__inner {
  font-family: inherit;
  line-height: 1.5;
}

.edit-project-dialog .dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style> 